import React, { useEffect, useState, useRef } from 'react'
import axios from '../../../utils/axios'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { ReactComponent as SignOutIcon } from '../../../assets/svgs/signout.svg'
import { ReactComponent as Overview } from '../../../assets/svgs/overview.svg'
import { ReactComponent as LInkedAccounts } from '../../../assets/svgs/link-accounts.svg'
import { ReactComponent as BusinessAccounts } from '../../../assets/svgs/user.svg'
import { ReactComponent as AdvertiserIcon } from '../../../assets/svgs/single-user.svg'
import { t } from '../../../utils/i18n'
import { Button } from '../../atoms/button/button'
import CustomDropdown from '../../molecules/dropdown/dropdown'
import { useAuth } from '../../../customHooks/useAuth'

import './side-nav.scss'
import { useStorage } from '../../../customHooks/useStorage'

interface PlatformProps {
  id: string
  name: string
}

interface PrivatePageProps {
  name: string
  icon: React.ReactElement
}

export const SideNav = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { value: activeStoredPlatform, setValue: setSelectedPlatform } = useStorage(
    'platform',
    { id: '', name: '' },
    'session'
  )

  const [platformList, setPlatformList] = useState<PlatformProps[] | []>([])
  const [activePlatform, setSetActivePlatform] = useState<PlatformProps>(activeStoredPlatform)
  const [allowedPrivateRoutes, setSetAllowedPrivateRoutes] = useState<PrivatePageProps[] | null>(
    null
  )
  const currentUserRole = user?.roles
  const PLATFORM_API = process.env.REACT_APP_PLATFORM_OPTIONS as string

  // Map paths to tab keys
  const pathToKey: Record<string, string> = {
    '/overview': 'overview',
    '/link-accounts': 'link-accounts',
    '/business-accounts': 'business-accounts',
    '/manage-advertiser': 'manage-advertiser',
  }

  const keyToPath: Record<string, string> = {
    overview: '/overview',
    'link-accounts': '/link-accounts',
    'business-accounts': '/business-accounts',
    'manage-advertiser': '/manage-advertiser',
  }

  const activeKey =
    Object.keys(pathToKey).find((path) => location.pathname.startsWith(path)) || '/overview'

  const handleSelect = (eventKey: string | null) => {
    if (eventKey && keyToPath[eventKey]) {
      navigate(keyToPath[eventKey])
    }
  }

  const getPlatformLists = async () => {
    try {
      const config = {
        method: 'GET',
        url: PLATFORM_API,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const platformListsRes = await axios(config)
      setPlatformList(platformListsRes.data.data.records)
      setSelectedPlatform(platformListsRes.data.data.records[0])
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        throw error.response ?? error
      }
      throw error
    }
  }

  function getPrivateRoutes(
    userRole: 'SUPERADMIN' | 'ADMIN' | 'EMPLOYEE' | undefined,
    isLinkAccountsSelected: PlatformProps | null
  ): PrivatePageProps[] {
    const role = Array.isArray(userRole) ? userRole[0]?.toLowerCase() : userRole?.toLowerCase()

    const routes = []

    if (activePlatform?.name?.toLowerCase() === 'meta' && isLinkAccountsSelected) {
      routes.push({
        name: 'link-accounts',
        icon: <LInkedAccounts />,
      })

      if (
        activePlatform?.name?.toLowerCase() === 'meta' &&
        (role === 'admin' || role === 'superadmin')
      ) {
        routes.push({
          name: 'business-accounts',
          icon: <BusinessAccounts />,
        })
      }
      if (
        activePlatform?.name?.toLowerCase() === 'meta' &&
        (role === 'admin' || role === 'superadmin')
      ) {
        routes.push({
          name: 'manage-advertiser',
          icon: <AdvertiserIcon />,
        })
      } else {
        navigate('/overview', { replace: true })
      }
    }
    return routes
  }

  const effectRan = useRef(false)
  const isFirstActivePlatformChange = useRef(true)

  // On mount: call getPlatformLists() only once (even in StrictMode)
  useEffect(() => {
    if (effectRan.current) return
    getPlatformLists()
    const routes = getPrivateRoutes(currentUserRole, activePlatform)
    if (routes) {
      setSetAllowedPrivateRoutes(routes)
    }
    effectRan.current = true
  }, [])

  // On activePlatform change (but not on initial mount): call getPlatformLists()
  useEffect(() => {
    if (isFirstActivePlatformChange.current) {
      isFirstActivePlatformChange.current = false
      return
    }
    getPlatformLists()
    const routes = getPrivateRoutes(currentUserRole, activePlatform)
    if (routes) {
      setSetAllowedPrivateRoutes(routes)
    }
  }, [activePlatform])

  const checkUserAccess = (): boolean => {
    if (user?.roles?.[0] === 'SUPERADMIN') {
      return true
    } else {
      return user?.businessUser as boolean
    }
  }

  const signOutHandler = () => {
    navigate('/signout')
  }

  return (
    <div className="sidebar-container">
      <div className="logo-container">
        <Link to={'/overview'} aria-label={t('brand.logo')} className="logo" />
      </div>
      <div className="sidebar-tabs">
        <div
          aria-label="overview"
          className={`body rs-tabs-tab ${activeKey === 'overview' ? 'rs-tabs-tab-active' : ''}`}
          onClick={() => handleSelect('overview')}
          tabIndex={0}
        >
          <div className="nav-icons">
            <Overview />
          </div>
          {t('overview.title')}
        </div>
        {checkUserAccess() ? (
          <CustomDropdown
            id="platform-selector"
            className="platform-dropdown"
            label="Select Platform"
            placeholder={activePlatform?.name ? activePlatform?.name : t('dropdown.placeholder')}
            options={platformList}
            onSelect={(option) => {
              setSetActivePlatform(option)
            }}
          />
        ) : null}
        {allowedPrivateRoutes?.map((app: PrivatePageProps, i: number) => (
          <div
            key={i}
            aria-label={app?.name}
            className={`body rs-tabs-tab ${activeKey === `/${app?.name}` ? 'rs-tabs-tab-active' : ''}`}
            onClick={() => handleSelect(app?.name)}
            tabIndex={0}
          >
            <div className="nav-icons">{app?.icon}</div>
            {t(`${app?.name}.page`)}
          </div>
        ))}
      </div>

      <Button
        className="sign-out"
        label={t('sign.out')}
        ButtonIcon={<SignOutIcon />}
        handleClick={signOutHandler}
      />
    </div>
  )
}
export default SideNav
